import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { fetchResources } from '../../features/resources/resourceSlice';
import ResourceCard from './ResourceCard';
import AddResourceForm from './AddResourceForm';
import Button from '../ui/Button';
import SearchInput from '../ui/SearchInput';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import { Resource } from '../../types/resources';

const ResourceList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { resources, loading, error } = useSelector((state: RootState) => state.resources);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Resource['category'] | 'all'>('all');

  useEffect(() => {
    dispatch(fetchResources());
  }, [dispatch]);

  const filteredResources = resources.filter(resource => {
    const matchesSearch = 
      resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || resource.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">{t('resources.title')}</h2>
        <Button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white transition-colors duration-200"
        >
          {showAddForm ? t('common.cancel') : t('resources.addResource')}
        </Button>
      </div>

      {showAddForm && (
        <AddResourceForm onSuccess={() => setShowAddForm(false)} />
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('common.search')}
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as Resource['category'] | 'all')}
          className="block w-full sm:w-48 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          aria-label={t('common.category')}
        >
          <option value="all">{t('common.all')}</option>
          <option value="seed">{t('resources.category.seed')}</option>
          <option value="fertilizer">{t('resources.category.fertilizer')}</option>
          <option value="pesticide">{t('resources.category.pesticide')}</option>
          <option value="tool">{t('resources.category.tool')}</option>
          <option value="other">{t('resources.category.other')}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredResources.length === 0 ? (
          <p className="text-gray-500 col-span-full text-center py-8">
            {t('resources.noResources')}
          </p>
        ) : (
          filteredResources.map((resource) => (
            <ResourceCard key={resource.id} resource={resource} />
          ))
        )}
      </div>
    </div>
  );
};

export default ResourceList;